/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductRefiner } from '@msdyn365-commerce/retail-proxy';
import { getWebGroupFiltersAsync } from './DataActionExtension.g';
import { CurrentCategoryInput, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { GetCategoryIdFromUrl, GetWebgroupIdFromUrl } from '../utilities';
import { GetCatalogIdFromContext } from '../utilities/get-catalog-id-from-context';

/**
 * Action Input class for the getRefinersByCategory data action.
 */
export class GetWebgroupFiltersInput implements IActionInput {
    public readonly catalogId: number;

    public readonly currentCategory: CurrentCategoryInput;

    public readonly queryResultSettingsProxy: QueryResultSettingsProxy;

    constructor(category: CurrentCategoryInput, queryResultSettingsProxy: QueryResultSettingsProxy, catalogId?: number) {
        this.queryResultSettingsProxy = queryResultSettingsProxy;
        this.catalogId = catalogId || 0;
        this.currentCategory = category;
    }

    public getCacheKey = () => `WebgroupFilters`;
    public getCacheObjectType = () => 'WebgroupFilters';
    public shouldCacheOutput = () => true;
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createGetWebgroupRefinersInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext) {
        const catalogId = getCatalogId(inputData.requestContext);
        const currentCategory = new CurrentCategoryInput(inputData.requestContext);

        return new GetWebgroupFiltersInput(currentCategory, QueryResultSettingsProxy.fromInputData(inputData), catalogId);
    }

    throw new Error('Please specify categoryId query string in request.');
};

/**
 * Calls the Retail API and returns all refiners by category.
 * @param input
 * @param ctx
 */
export async function getWebgroupRefinersAction(input: GetWebgroupFiltersInput, ctx: IActionContext): Promise<ProductRefiner[]> {
    const categyoryId = GetCategoryIdFromUrl(ctx);
    const webgroupId = GetWebgroupIdFromUrl(ctx);
    if (!categyoryId && !webgroupId) {
        throw new Error('No category or webgroup Id provided');
    }
    const catalogId = GetCatalogIdFromContext(ctx).toString();
    return getWebGroupFiltersAsync({ callerContext: ctx }, categyoryId, webgroupId, catalogId)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[get-webgroup-filters] Unable to load webgroup filters');
            throw new Error('[get-webgroup-filters] Unable to load webgroup filters');
        });
}

export default createObservableDataAction({
    id: 'get-webgroup-filters',
    action: <IAction<ProductRefiner[]>>getWebgroupRefinersAction,
    input: createGetWebgroupRefinersInput
});
